import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Search } from "lucide-react";

import { carePlansApi } from "@/api/careplans";
import { Alert } from "@/components/Alert";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { FormSelect } from "@/components/FormSelect";
import { ListToolbar } from "@/components/ListToolbar";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function statusVariant(status) {
  if (status === "active") return "default";
  if (status === "paused") return "outline";
  return "secondary";
}

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "personal_care", label: "Personal Care" },
  { value: "medication", label: "Medication" },
  { value: "mobility", label: "Mobility" },
  { value: "companionship", label: "Companionship" },
  { value: "nursing", label: "Nursing" },
  { value: "custom", label: "Custom" },
];

const COLUMNS = [
  {
    key: "client",
    header: "Client",
    render: (row) => (
      <Link to={`/clients/${row.client}`} className="font-medium text-primary hover:underline">
        {row.client_name}
      </Link>
    ),
  },
  {
    key: "title",
    header: "Care Plan Title",
    render: (row) => <span className="font-medium">{row.title}</span>,
  },
  {
    key: "category",
    header: "Category",
    render: (row) => (
      <Badge variant="outline">{row.category_display || row.category}</Badge>
    ),
  },
  { key: "start_date", header: "Start Date", render: (row) => row.start_date },
  {
    key: "status",
    header: "Status",
    render: (row) => (
      <Badge variant={statusVariant(row.status)} className="capitalize">
        {row.status_display || row.status}
      </Badge>
    ),
  },
  {
    key: "tasks",
    header: "Tasks",
    render: (row) => `${row.tasks?.length || 0} items`,
  },
  {
    key: "template",
    header: "Template",
    render: (row) => (
      <span className="text-muted-foreground">{row.template_name || "Custom"}</span>
    ),
  },
];

export function ClientCarePlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (status) params.status = status;
      if (category) params.category = category;
      const res = await carePlansApi.list(params);
      setPlans(res.results || []);
    } catch (err) {
      setError(err.message || "Failed to load care plans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, [search, status, category]);

  if (loading && plans.length === 0) {
    return <PageSkeleton />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Client Care Plans"
        description="Manage client care plans across all active clients."
        actions={
          <Button asChild variant="outline">
            <Link to="/care-plans/templates">Manage Templates</Link>
          </Button>
        }
      />

      <PageBody>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by plan title or client…"
          searchAriaLabel="Search care plans"
        >
          <div className="w-full max-w-xs">
            <FormSelect
              id="care-plans-status-filter"
              value={status}
              onValueChange={setStatus}
              options={STATUS_OPTIONS}
              placeholder="All Statuses"
            />
          </div>
          <div className="w-full max-w-xs">
            <FormSelect
              id="care-plans-category-filter"
              value={category}
              onValueChange={setCategory}
              options={CATEGORY_OPTIONS}
              placeholder="All Categories"
            />
          </div>
        </ListToolbar>

        <Alert>{error}</Alert>

        {plans.length === 0 ? (
          <EmptyState
            icon={search || status || category ? Search : ClipboardList}
            tone={search || status || category ? "slate" : "cyan"}
            title={
              search || status || category
                ? "No care plans match those filters."
                : "No care plans yet"
            }
            message={
              search || status || category
                ? "Try adjusting search, status, or category."
                : "Assign a template from a client record, or browse templates to get started."
            }
            action={
              search || status || category ? null : (
                <Button asChild variant="outline">
                  <Link to="/care-plans/templates">Manage templates</Link>
                </Button>
              )
            }
          />
        ) : (
          <div aria-busy={loading}>
            <DataTable columns={COLUMNS} rows={plans} getRowKey={(row) => row.id} />
          </div>
        )}
      </PageBody>
    </PageShell>
  );
}
